import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import SectionHeader from './SectionHeader';
import { Filter } from 'lucide-react';
import api from '../../../api/axios'; // <-- use your configured api

const ConcernProductAnalysis = () => {
  const [dateFilter, setDateFilter] = useState('Last 7 Days');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [concernFilter, setConcernFilter] = useState('All');

  const [products, setProducts] = useState([]);
  const [concernCategories, setConcernCategories] = useState(['All']);

  /* -------------------- DATE RANGE UTILS -------------------- */
  const getFromToDates = (filter) => {
    const now = new Date();
    let from, to;

    to = new Date(now);
    to.setHours(23, 59, 59, 0);

    switch (filter) {
      case 'Today':
        from = new Date(now);
        from.setHours(0, 0, 0, 0);
        break;

      case 'Yesterday':
        from = new Date(now);
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        to = new Date(from);
        to.setHours(23, 59, 59, 0);
        break;

      case 'Last 7 Days':
        from = new Date(now);
        from.setDate(from.getDate() - 6);
        from.setHours(0, 0, 0, 0);
        break;

      case 'Last 14 Days':
        from = new Date(now);
        from.setDate(from.getDate() - 13);
        from.setHours(0, 0, 0, 0);
        break;

      case 'Last Month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;

      case 'Custom Range':
        from = new Date(customStartDate);
        from.setHours(0, 0, 0, 0);
        to = new Date(customEndDate);
        to.setHours(23, 59, 59, 0);
        break;

      default:
        return null;
    }

    const format = (d) => d.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss
    return { from: format(from), to: format(to) };
  };

  /* -------------------- API -------------------- */
  const fetchConcernProducts = async () => {
    try {
      const range = getFromToDates(dateFilter);
      if (!range) return;

      const params = { from: range.from, to: range.to };
      if (concernFilter !== 'All') params.concern = concernFilter;

      const res = await api.get('/api/v1/dashboard/concern-products', { params });
      const { productMetricsList, concernList } = res.data.data;

      setConcernCategories(['All', ...concernList]);

      const mappedProducts = productMetricsList.map((item, index) => ({
        id: index + 1,
        name: item.productName,
        category: item.concern,
        calls: item.totalCalls,
        avgCallDuration: +(item.avgCallDuration / 60).toFixed(2),
        resolutionRate: item.aiResolutionRate,
        humanEscalation: item.humanEscalationPercent,
        percentage: item.percentage,
        trend: item.trend ?? 0,
      }));

      setProducts(mappedProducts);
    } catch (error) {
      console.error('Failed to fetch concern products', error);
    }
  };

  useEffect(() => {
    fetchConcernProducts();
  }, [concernFilter, dateFilter]);

  /* -------------------- HANDLERS -------------------- */
  const handleDateChange = (value) => {
    setDateFilter(value);
    setShowCustomRange(value === 'Custom Range');
  };

  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) return;
    setShowCustomRange(false);
    fetchConcernProducts();
  };

  const getTrendIcon = (trend) => (trend > 0 ? 'TrendingUp' : trend < 0 ? 'TrendingDown' : 'Minus');
  const getTrendColor = (trend) => (trend > 0 ? 'text-error' : trend < 0 ? 'text-success' : 'text-muted-foreground');

  /* -------------------- UI -------------------- */
  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Top Product Concerns</h3>
          <p className="text-sm text-muted-foreground">Most frequent issues by product</p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <select
            value={concernFilter}
            onChange={(e) => setConcernFilter(e.target.value)}
            className="appearance-none bg-card border border-border rounded-lg px-3 py-1.5 pr-8 text-xs font-medium text-foreground hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
          >
            {concernCategories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <SectionHeader
          dateFilter={dateFilter}
          onDateChange={handleDateChange}
          dateOptions={['Today', 'Yesterday', 'Last 7 Days', 'Last 14 Days', 'Last Month', 'Custom Range']}
        />
      </div>

      {/* Custom Date Range */}
      {showCustomRange && (
        <div className="bg-background border border-border rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={handleApplyCustomRange}
              disabled={!customStartDate || !customEndDate}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((product) => (
          <div
            key={product.id}
            className="relative bg-background border border-border rounded-lg p-4 transition hover:shadow-md"
            onMouseEnter={() => setHoveredId(product.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            {hoveredId === product.id && (
              <div className="absolute z-20 top-2 right-2 w-64 bg-card border border-border rounded-lg shadow-lg p-4">
                <div className="font-semibold text-foreground mb-2 pb-2 border-b border-border">{product.name}</div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-muted-foreground">Category</span><span className="font-medium">{product.category}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Total Calls</span><span className="font-medium">{product.calls}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Avg Duration</span><span className="font-medium">{product.avgCallDuration} min</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Autonomous call Completion Rate</span><span className="font-medium text-green-600">{product.resolutionRate}%</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Human Escalation</span><span className="font-medium text-orange-600">{product.humanEscalation}%</span></div>
                </div>
              </div>
            )}

            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-foreground mb-1">{product.name}</h4>
                <p className="text-xs text-muted-foreground">{product.category}</p>
              </div>
              <Icon name={getTrendIcon(product.trend)} size={18} className={getTrendColor(product.trend)} />
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Calls</span><span className="font-medium">{product.calls}</span></div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="h-full bg-primary rounded-full" style={{ width: `${product.percentage}%` }} />
              </div>
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Autonomous call Completion Rate</span><span className="font-medium">{product.resolutionRate}%</span></div>
            </div>

            <div className="flex justify-between pt-3 border-t border-border">
              <div className="flex items-center gap-1">
                <Icon name={getTrendIcon(product.trend)} size={14} className={getTrendColor(product.trend)} />
                <span className={`text-xs font-medium ${getTrendColor(product.trend)}`}>{product.trend > 0 ? '+' : ''}{product.trend}%</span>
              </div>
              <span className="text-xs text-muted-foreground">vs last period</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConcernProductAnalysis;
